import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { reportsApi } from '../../api/reports.api';
import { downloadFile, generateFilename } from '../../utils/download';
import { ReportFilters } from '../../types/report';

interface ExportButtonsProps {
  filters?: ReportFilters;
}

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

export const ExportButtons: React.FC<ExportButtonsProps> = ({ filters = {} }) => {
  const { t } = useTranslation();
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);

  // Build a human-readable hint showing which period is being exported.
  const periodHint = (() => {
    if (filters.type === 'annual' && filters.year) {
      return t('export.hintYear', { year: filters.year });
    }
    if (filters.month) {
      const [yearStr, monthStr] = filters.month.split('-');
      const monthIndex = parseInt(monthStr, 10) - 1;
      const monthLabel = t(`reports.months.${MONTH_KEYS[monthIndex]}`);
      return t('export.hintMonth', { month: monthLabel, year: yearStr });
    }
    return null;
  })();

  const buildFilenamePrefix = (ext: string) => {
    if (filters.type === 'annual' && filters.year) {
      return generateFilename(`rapport-annuel-${filters.year}`, ext);
    }
    if (filters.month) {
      return generateFilename(`rapport-mensuel-${filters.month}`, ext);
    }
    return generateFilename('rapport', ext);
  };

  const handleExportExcel = async () => {
    try {
      setIsExportingExcel(true);
      const blob = await reportsApi.exportExcel(filters);
      downloadFile(blob, buildFilenamePrefix('xlsx'));
    } catch (error) {
      console.error('Excel export failed:', error);
      alert(t('export.excelFailed'));
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setIsExportingCSV(true);
      const blob = await reportsApi.exportCSV(filters);
      downloadFile(blob, buildFilenamePrefix('csv'));
    } catch (error) {
      console.error('CSV export failed:', error);
      alert(t('export.csvFailed'));
    } finally {
      setIsExportingCSV(false);
    }
  };

  const busy = isExportingExcel || isExportingCSV;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      {periodHint && (
        <span className="text-xs text-gray-500 italic hidden sm:inline">{periodHint}</span>
      )}
      <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={handleExportExcel}
          isLoading={isExportingExcel}
          disabled={busy}
        >
          <span className="mr-2">📊</span>
          {t('export.exportExcel')}
        </Button>
        <Button
          variant="secondary"
          onClick={handleExportCSV}
          isLoading={isExportingCSV}
          disabled={busy}
        >
          <span className="mr-2">📄</span>
          {t('export.exportCSV')}
        </Button>
      </div>
    </div>
  );
};
